import { signInWithGoogle as firebaseSignInWithGoogle } from './authService';
import { signInViaSafariRelay, isAccountUpgradePage, ACCOUNT_UPGRADE_URL } from './safariRelayAuth';
import {
    GoogleAuthProvider,
    indexedDBLocalPersistence,
    setPersistence,
    signInWithCredential
} from 'firebase/auth/web-extension';
import { auth } from '../config/firebaseConfig';
import { log, error as logError } from '../utils/logger';
import { ext, isExtensionPage } from '../utils/browserApi';

export interface ChromeIdentityAuthResult {
    user: {
        uid: string;
        displayName: string | null;
        email: string | null;
        photoURL: string | null;
        providerId: string;
    } | null;
    error?: string;
}

// OAuth2 client ID — set REACT_APP_GOOGLE_CLIENT_ID in your .env
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/**
 * Check if we're running in a Chrome or Safari extension context with identity support.
 */
const isExtensionContext = (): boolean =>
    isExtensionPage() && typeof (ext as any)?.identity?.launchWebAuthFlow === 'function';

/**
 * Clear any cached token to force re-authentication.
 * Chrome only — Safari's identity API does not support getAuthToken.
 */
const clearCachedToken = (): Promise<void> => {
    return new Promise((resolve) => {
        if (!(ext as any)?.identity?.getAuthToken) {
            resolve();
            return;
        }

        (ext as any).identity.getAuthToken({ interactive: false }, (token: string) => {
            if (ext.runtime?.lastError || !token) {
                resolve();
                return;
            }

            log('[ChromeIdentity] Clearing cached token to force account picker...');
            (ext as any).identity.removeCachedAuthToken({ token }, () => {
                resolve();
            });
        });
    });
};

/**
 * Sign in with Google using the browser Identity API with account picker.
 * Uses launchWebAuthFlow (supported in both Chrome and Safari).
 * Falls back to Firebase Auth when not in extension context (e.g., localhost development).
 */
export const signInWithChromeIdentity = async (): Promise<ChromeIdentityAuthResult> => {
    try {
        log('[ChromeIdentity] Starting sign-in with account picker...');
        log('[ChromeIdentity] Protocol:', window.location.protocol);
        log('[ChromeIdentity] Is extension context:', isExtensionContext());

        if (!isExtensionContext()) {
            if (isExtensionPage()) {
                // Extension page but no identity API (e.g. Safari) — use hosted relay.
                log('[ChromeIdentity] Safari extension: using relay sign-in...');
                return signInViaSafariRelay('google');
            }
            if (isAccountUpgradePage()) {
                // Hosted upgrade page: popup OAuth is dev-only, so go through the
                // same hosted relay as Safari, returning to this exact page.
                log('[ChromeIdentity] Account upgrade page: using relay sign-in...');
                return signInViaSafariRelay('google', ACCOUNT_UPGRADE_URL);
            }
            log('[ChromeIdentity] Not in extension context, using Firebase Auth fallback...');
            const result = await firebaseSignInWithGoogle();
            return result;
        }

        await clearCachedToken();

        const redirectUrl = (ext as any).identity.getRedirectURL();
        log('[ChromeIdentity] Redirect URL:', redirectUrl);

        const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
        authUrl.searchParams.set('client_id', CLIENT_ID);
        authUrl.searchParams.set('redirect_uri', redirectUrl);
        authUrl.searchParams.set('response_type', 'token');
        authUrl.searchParams.set('scope', 'email profile');
        authUrl.searchParams.set('prompt', 'select_account');

        log('[ChromeIdentity] Launching web auth flow...');

        return new Promise((resolve, reject) => {
            (ext as any).identity.launchWebAuthFlow(
                { url: authUrl.toString(), interactive: true },
                async (responseUrl: string) => {
                    if (ext.runtime?.lastError) {
                        logError('[ChromeIdentity] Auth flow failed:', ext.runtime.lastError);
                        reject(new Error((ext.runtime.lastError as any).message || 'Authentication failed'));
                        return;
                    }

                    if (!responseUrl) {
                        reject(new Error('No response from authentication flow'));
                        return;
                    }

                    log('[ChromeIdentity] Auth flow completed, extracting token...');

                    const url = new URL(responseUrl);
                    const hashParams = new URLSearchParams(url.hash.substring(1));
                    const accessToken = hashParams.get('access_token');

                    if (!accessToken) {
                        reject(new Error('No access token in response'));
                        return;
                    }

                    log('[ChromeIdentity] Got access token, fetching user info...');

                    try {
                        const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
                            headers: { 'Authorization': `Bearer ${accessToken}` }
                        });

                        if (!response.ok) {
                            throw new Error(`Failed to fetch user info: ${response.statusText}`);
                        }

                        const googleUser = await response.json();
                        log('[ChromeIdentity] User info retrieved successfully');

                        await setPersistence(auth, indexedDBLocalPersistence);
                        const credential = GoogleAuthProvider.credential(null, accessToken);
                        const firebaseResult = await signInWithCredential(auth, credential);

                        resolve({
                            user: {
                                uid: firebaseResult.user.uid,
                                displayName: firebaseResult.user.displayName || googleUser.name || null,
                                email: firebaseResult.user.email || googleUser.email || null,
                                photoURL: firebaseResult.user.photoURL || googleUser.picture || null,
                                providerId: firebaseResult.user.providerData[0]?.providerId || 'google.com',
                            },
                        });
                    } catch (fetchError: any) {
                        logError('[ChromeIdentity] Failed to fetch user info:', fetchError);
                        reject(new Error(`Failed to get user info: ${fetchError.message || String(fetchError)}`));
                    }
                }
            );
        });
    } catch (error) {
        logError('[ChromeIdentity] Sign-in error:', error);
        return {
            user: null,
            error: error instanceof Error ? error.message : String(error),
        };
    }
};

const chromeIdentityAuth = { signInWithChromeIdentity };
export default chromeIdentityAuth;
